import React, { useState } from 'react';
import { usePS2Graphics } from '../context/PS2GraphicsContext';
import { GolfFlagIcon, TrophyIcon } from './Icons';

const GOLF_HOLES = [
  { hole: 1, par: 4, yards: 410, name: "Emerald Tee-Off", handicap: 7 },
  { hole: 2, par: 3, yards: 185, name: "Sand Trap Challenge", handicap: 15 },
  { hole: 3, par: 5, yards: 535, name: "Water Hazard Bend", handicap: 1 },
  { hole: 4, par: 4, yards: 395, name: "Pine Tree Alley", handicap: 9 },
  { hole: 5, par: 4, yards: 420, name: "PS2 Memory Gulch", handicap: 3 },
  { hole: 6, par: 3, yards: 160, name: "128-Bit Green", handicap: 17 },
  { hole: 7, par: 5, yards: 550, name: "Fairway Boulevard", handicap: 5 },
  { hole: 8, par: 4, yards: 440, name: "Bunker Ridge", handicap: 11 },
  { hole: 9, par: 4, yards: 380, name: "Clubhouse Turn", handicap: 13 },
  { hole: 10, par: 4, yards: 405, name: "19th Hole Approach", handicap: 8 },
  { hole: 11, par: 3, yards: 195, name: "Emotion Engine Drop", handicap: 16 },
  { hole: 12, par: 5, yards: 570, name: "Smashburger Straight", handicap: 2 },
  { hole: 13, par: 4, yards: 415, name: "Transfusion Creek", handicap: 10 },
  { hole: 14, par: 4, yards: 390, name: "Tiger Woods Ridge", handicap: 12 },
  { hole: 15, par: 3, yards: 175, name: "Eagle Pin Hole", handicap: 18 },
  { hole: 16, par: 4, yards: 430, name: "Retro Memory Run", handicap: 6 },
  { hole: 17, par: 5, yards: 560, name: "Snack Bar Summit", handicap: 4 },
  { hole: 18, par: 4, yards: 450, name: "Grand Clubhouse Finish", handicap: 14 },
];

export default function GolfCourseWidget() {
  const { ps2Mode, playPs2Click, playPs2Select } = usePS2Graphics();
  const [selectedHole, setSelectedHole] = useState(0);
  const [scores, setScores] = useState(Array(18).fill(null));
  const [showScorecard, setShowScorecard] = useState(false);

  const currentHole = GOLF_HOLES[selectedHole];

  const setHoleScore = (holeIdx, delta) => {
    playPs2Click();
    setScores(prev => {
      const copy = [...prev];
      const currentVal = copy[holeIdx] ?? GOLF_HOLES[holeIdx].par;
      const newVal = Math.max(1, currentVal + delta);
      copy[holeIdx] = newVal;
      return copy;
    });
  };

  const totalPar = GOLF_HOLES.reduce((acc, h) => acc + h.par, 0);
  const totalScore = scores.reduce((acc, s, idx) => acc + (s ?? GOLF_HOLES[idx].par), 0);
  const scoreDiff = totalScore - totalPar;

  return (
    <div aria-label="Golf Course Weather & Scorecard" className="w-full max-w-5xl mx-auto px-4 my-8">
      {/* Fairway Banner Pill */}
      <div
        className="p-4 md:p-6 rounded-2xl border backdrop-blur-xl transition-all duration-500 relative overflow-hidden"
        style={{
          background: ps2Mode
            ? 'linear-gradient(135deg, rgba(6,182,212,0.1), rgba(15,23,42,0.85))'
            : 'linear-gradient(135deg, rgba(20,83,45,0.35), rgba(6,78,59,0.2), rgba(0,0,0,0.8))',
          borderColor: ps2Mode ? 'rgba(6,182,212,0.3)' : 'rgba(34,197,94,0.25)',
          boxShadow: ps2Mode
            ? '0 8px 32px rgba(6,182,212,0.15)'
            : '0 8px 32px rgba(0,0,0,0.4)',
        }}
      >
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6 relative z-10">
          {/* Weather & Hole info */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0"
              style={{
                background: ps2Mode
                  ? 'linear-gradient(135deg, rgba(6,182,212,0.3), rgba(59,130,246,0.1))'
                  : 'linear-gradient(135deg, rgba(34,197,94,0.2), rgba(16,185,129,0.05))',
                border: ps2Mode ? '1px solid rgba(6,182,212,0.4)' : '1px solid rgba(34,197,94,0.3)',
              }}
            >
              <GolfFlagIcon className="w-7 h-7 text-emerald-400" />
            </div>

            <div>
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                <span className="text-[10px] font-mono tracking-widest uppercase text-emerald-400 font-bold px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20">
                  LIVE COURSE STATUS
                </span>
                <span className="text-[10px] font-mono text-white/40">
                  ST. ANDREWS FAIRWAYS
                </span>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-white font-mono">
                Hole {currentHole.hole}: {currentHole.name}
              </h3>
              <p className="text-xs text-white/60 font-mono mt-0.5">
                Par {currentHole.par} · {currentHole.yards} Yds · Handicap Hdcp {currentHole.handicap}
              </p>
            </div>
          </div>

          {/* Quick Metrics Bar */}
          <div className="flex items-center gap-3 font-mono text-xs text-white/80 flex-wrap justify-center">
            <div className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-center">
              <div className="text-[9px] text-white/40 uppercase">Weather</div>
              <div className="font-bold text-emerald-300">72°F Clear</div>
            </div>
            <div className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-center">
              <div className="text-[9px] text-white/40 uppercase">Wind</div>
              <div className="font-bold text-cyan-300">8 mph SE</div>
            </div>
            <div className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-center">
              <div className="text-[9px] text-white/40 uppercase">Green Speed</div>
              <div className="font-bold text-purple-300">11.5 Fast</div>
            </div>
            <div className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-center">
              <div className="text-[9px] text-white/40 uppercase">Score to Par</div>
              <div className={`font-bold ${scoreDiff <= 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
                {scoreDiff > 0 ? `+${scoreDiff}` : scoreDiff === 0 ? 'E' : scoreDiff}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setSelectedHole(prev => (prev + 1) % 18);
                playPs2Click();
              }}
              className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white font-mono text-xs border border-white/15 transition-all cursor-pointer"
            >
              Next Hole →
            </button>
            <button
              onClick={() => {
                setShowScorecard(prev => !prev);
                playPs2Select();
              }}
              className="px-4 py-2 rounded-xl font-mono text-xs font-bold text-white transition-all shadow-lg flex items-center gap-1.5 cursor-pointer"
              style={{
                background: ps2Mode
                  ? 'linear-gradient(135deg, rgba(6,182,212,0.4), rgba(59,130,246,0.3))'
                  : 'linear-gradient(135deg, #16a34a, #15803d)',
              }}
            >
              <TrophyIcon className="w-3.5 h-3.5" />
              <span>{showScorecard ? 'Hide Card' : 'Scorecard'}</span>
            </button>
          </div>
        </div>

        {/* Interactive Scorecard Grid Drawer */}
        {showScorecard && (
          <div className="mt-6 pt-6 border-t border-white/10 animate-boot-1">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-mono font-bold text-emerald-400 flex items-center gap-2">
                <TrophyIcon className="w-4 h-4 text-amber-400" />
                <span>Official 18-Hole Country Club Scorecard</span>
              </h4>
              <span className="text-xs font-mono text-white/40">
                Total Yards: {GOLF_HOLES.reduce((a, b) => a + b.yards, 0)} Yds
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono text-center border-collapse">
                <thead>
                  <tr className="border-b border-white/10 text-white/40">
                    <th className="p-2 text-left">Hole</th>
                    {GOLF_HOLES.slice(0, 9).map(h => (
                      <th key={h.hole} className="p-2">{h.hole}</th>
                    ))}
                    <th className="p-2 text-emerald-300 font-bold">OUT</th>
                    {GOLF_HOLES.slice(9, 18).map(h => (
                      <th key={h.hole} className="p-2">{h.hole}</th>
                    ))}
                    <th className="p-2 text-emerald-300 font-bold">IN</th>
                    <th className="p-2 text-white font-bold">TOT</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Par row */}
                  <tr className="border-b border-white/5 text-white/50">
                    <td className="p-2 text-left font-bold">Par</td>
                    {GOLF_HOLES.slice(0, 9).map(h => (
                      <td key={h.hole} className="p-2">{h.par}</td>
                    ))}
                    <td className="p-2 font-bold text-white/70">
                      {GOLF_HOLES.slice(0, 9).reduce((a, b) => a + b.par, 0)}
                    </td>
                    {GOLF_HOLES.slice(9, 18).map(h => (
                      <td key={h.hole} className="p-2">{h.par}</td>
                    ))}
                    <td className="p-2 font-bold text-white/70">
                      {GOLF_HOLES.slice(9, 18).reduce((a, b) => a + b.par, 0)}
                    </td>
                    <td className="p-2 font-bold text-white">{totalPar}</td>
                  </tr>
                  {/* Score row */}
                  <tr className="text-white">
                    <td className="p-2 text-left font-bold text-emerald-400">Score</td>
                    {GOLF_HOLES.slice(0, 9).map((h, idx) => (
                      <td key={h.hole} className="p-1">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setHoleScore(idx, -1)}
                            className="w-4 h-4 rounded bg-white/10 hover:bg-white/20 text-[10px]"
                          >
                            -
                          </button>
                          <span className="font-bold text-xs">{scores[idx] ?? h.par}</span>
                          <button
                            onClick={() => setHoleScore(idx, 1)}
                            className="w-4 h-4 rounded bg-white/10 hover:bg-white/20 text-[10px]"
                          >
                            +
                          </button>
                        </div>
                      </td>
                    ))}
                    <td className="p-2 font-bold text-emerald-300">
                      {scores.slice(0, 9).reduce((a, s, idx) => a + (s ?? GOLF_HOLES[idx].par), 0)}
                    </td>
                    {GOLF_HOLES.slice(9, 18).map((h, idx) => {
                      const realIdx = idx + 9;
                      return (
                        <td key={h.hole} className="p-1">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setHoleScore(realIdx, -1)}
                              className="w-4 h-4 rounded bg-white/10 hover:bg-white/20 text-[10px]"
                            >
                              -
                            </button>
                            <span className="font-bold text-xs">{scores[realIdx] ?? h.par}</span>
                            <button
                              onClick={() => setHoleScore(realIdx, 1)}
                              className="w-4 h-4 rounded bg-white/10 hover:bg-white/20 text-[10px]"
                            >
                              +
                            </button>
                          </div>
                        </td>
                      );
                    })}
                    <td className="p-2 font-bold text-emerald-300">
                      {scores.slice(9, 18).reduce((a, s, idx) => a + (s ?? GOLF_HOLES[idx + 9].par), 0)}
                    </td>
                    <td className="p-2 font-bold text-emerald-400 text-sm">{totalScore}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
